// CSV exporter for form responses (Story saas-1.15).
// Wires up the previously broken "Exportar CSV" button in FormResponses.tsx
// by generating an RFC 4180-compliant CSV with UTF-8 BOM (Excel PT-BR
// compatibility) and triggering a download.
//
// Decisions (Pax gate v1.1):
// - OQ1=a: standalone lib/csv/ module for symmetry with lib/pdf/pdfExporter.ts
// - OQ3=a: UTF-8 with BOM — Excel PT-BR renders accents correctly
// - OQ4=b: metadata columns = created_at + status (2 cols)
// - OQ5=a: multi-value delimiter = "; " (semicolon + space)

import type { FormField, FormResponse, Form } from '../../types/forms';
import { NON_INPUT_FIELD_TYPES } from '../../types/forms';
import { sanitizeFilenameTitle } from '../utils/filename';

const STATUS_LABELS: Record<string, string> = {
  new: 'Novo',
  in_progress: 'Em andamento',
  completed: 'Concluído',
};

// Escape a single cell per RFC 4180:
// - null/undefined → empty string
// - if contains ", , \n, or \r → wrap in double-quotes and double any internal quotes
function escapeCSVCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// Format a response value per its field type.
// Multi-value fields joined with "; " (OQ5=a). Files render as URL strings.
// Special/non-input fields never reach here (filtered before).
function formatFieldValueForCSV(field: FormField, value: unknown): string {
  if (value === null || value === undefined) return '';

  // Arrays (checkbox, multiselect, picture_choice multi)
  if (Array.isArray(value)) {
    return value
      .map((v) => (v === null || v === undefined ? '' : String(v)))
      .filter((v) => v.length > 0)
      .join('; ');
  }

  // picture_choice single selection may arrive as an object shape in some data;
  // coerce to string defensively.
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  // Primitives (string, number, boolean) — rely on the field type implicitly.
  // yes_no may arrive as boolean; keep the user's literal string when possible.
  void field;
  return String(value);
}

/**
 * Export the given responses as a CSV download.
 * Filename: `{sanitizedTitle}_respostas_{YYYY-MM-DD}.csv`
 * Respects whatever filter produced `responses` (OQ2=b — hook already filters).
 */
export function exportResponsesToCSV(responses: FormResponse[], form: Form): void {
  // Columns: 2 metadata + one per input field (special screens filtered out).
  const inputFields = (form.fields || []).filter(
    (f) => !NON_INPUT_FIELD_TYPES.includes(f.type)
  );

  const headers = [
    'Data de criação',
    'Status',
    ...inputFields.map((f) => f.label || f.id),
  ];

  const rows: string[] = [];
  rows.push(headers.map(escapeCSVCell).join(','));

  for (const response of responses) {
    const cells: string[] = [];
    cells.push(escapeCSVCell(response.created_at));
    cells.push(escapeCSVCell(STATUS_LABELS[response.status] ?? response.status));
    for (const field of inputFields) {
      const value = response.data?.[field.id];
      cells.push(escapeCSVCell(formatFieldValueForCSV(field, value)));
    }
    rows.push(cells.join(','));
  }

  // RFC 4180 uses \r\n between rows; prefix UTF-8 BOM for Excel PT-BR (OQ3=a).
  const csvContent = '\uFEFF' + rows.join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const isoDate = new Date().toISOString().slice(0, 10);
  const filename = `${sanitizeFilenameTitle(form.title)}_respostas_${isoDate}.csv`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
